import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useJobColors } from "../../../../hooks/useJobColors";
import { BADGE, INK } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { activateOnKey } from "./a11y";
import { MONTHLY_CELL_ROW_HEIGHT, type MonthlyJobCount, type MonthlyShiftWindow, monthlyWindowLabel } from "./monthlyJobUtils";
import { getWindowPresentation } from "./monthlyWindowPresentation";

type Props = {
	window: MonthlyShiftWindow;
	/** Translated copy for the band-less `Other` overflow bucket. */
	otherLabel: string;
	count: number;
	/** Per-job split inside this window. Empty in "By shift" mode, where job is not a dimension. */
	jobs: readonly MonthlyJobCount[];
	/** Numbers when the measured row height allows; otherwise swatch dots with the numbers on hover. */
	showJobNumbers: boolean;
	/** Already-translated tooltip/aria text naming the window, its total, and its per-job split. */
	description: string;
	onClick: () => void;
	dataTestId: string;
};

/**
 * One shift-time window inside a grid cell — the second, quieter tier under the day's date/amount row.
 *
 * Built to the SAME row grammar as the `By job` count pill next door (`CountPill` in `MonthlyJobCell`),
 * because the two are breakdowns of one tile and used to read as two different screens: one shared
 * `MONTHLY_CELL_ROW_HEIGHT`, the same pill radius and horizontal padding, the same label/count type
 * scale, and the count pushed to the row's right edge so a column of tiles reads as one ledger whichever
 * mode is on. The count sits LAST — after any job swatches — so that right edge is the number's, always.
 *
 * What stays different is only what genuinely carries shift-time meaning: the band tint as the row's
 * background and the sun/moon glyph in the slot where a job row puts its colour dot. The 3px left rail
 * that used to sit under the tint is gone — it read as a seam across a radiused pill and duplicated a
 * band signal the tint and the glyph already carry twice.
 *
 * The two dimensions still sit on DIFFERENT visual channels so they can never be confused: the WINDOW is
 * position (one row, ordered by start) + tint + glyph, while the JOB is hue (`useJobColors` swatches nested
 * inside the row). Numbers are tabular so a column of rows reads as one ledger.
 */
export const MonthlyWindowRow: FC<Props> = ({ window, otherLabel, count, jobs, showJobNumbers, description, onClick, dataTestId }) => {
	const jobColor = useJobColors();
	const { tint, ink, glyph } = getWindowPresentation(window);

	return (
		<InHouseTooltip title={description}>
			<Box
				data-testid={dataTestId}
				role="button"
				tabIndex={0}
				aria-label={description}
				onClick={(event) => {
					event.stopPropagation();
					onClick();
				}}
				onKeyDown={(event) => {
					event.stopPropagation();
					activateOnKey(onClick)(event);
				}}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 0.5,
					flex: "0 0 auto",
					height: `${MONTHLY_CELL_ROW_HEIGHT}px`,
					px: 0.75,
					borderRadius: `${BADGE.PILL_RADIUS}px`,
					overflow: "hidden",
					cursor: "pointer",
					backgroundColor: tint,
					transition: "filter 120ms ease",
					"@media (hover: hover) and (pointer: fine)": { "&:hover": { filter: "brightness(0.96)" } },
				}}
			>
				{/* Glyph + label: the same left group a job pill builds from its colour dot + job name. */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
					{glyph}
					<Typography
						sx={{
							fontSize: "12px",
							fontWeight: 500,
							lineHeight: 1,
							color: ink,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							minWidth: 0,
						}}
					>
						{monthlyWindowLabel(window, otherLabel)}
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
					{jobs.length > 0 && (
						<Box sx={{ display: "flex", alignItems: "center", gap: showJobNumbers ? 0.5 : "3px", flexShrink: 0 }}>
							{jobs.map((job) =>
								showJobNumbers ? (
									<Box key={job.jobId} sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
										<Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: jobColor(job.jobId).dot, flexShrink: 0 }} />
										<Typography sx={{ fontSize: "10px", fontWeight: 600, lineHeight: 1, color: INK.SECONDARY, fontVariantNumeric: "tabular-nums" }}>
											{job.count}
										</Typography>
									</Box>
								) : (
									<Box key={job.jobId} sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: jobColor(job.jobId).dot, flexShrink: 0 }} />
								),
							)}
						</Box>
					)}
					<Typography sx={{ fontSize: "13px", fontWeight: 700, lineHeight: 1, color: ink, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
						{count}
					</Typography>
				</Box>
			</Box>
		</InHouseTooltip>
	);
};
